import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import TimetableEditor from './TimetableEditor';
import './TimetableManagementScreen.css';

interface YearGroupTimetable {
    id: string;
    yearGroup: string;
    term: string;
    termStartDate: string;
    termWeeks: number;
    weeks: Record<number, any[]>;
    classrooms: any[];
    createdAt: any;
    updatedAt: any;
}

interface TimetableManagementScreenProps {
    organizationCode: string;
    onClose: () => void;
}

const TimetableManagementScreen: React.FC<TimetableManagementScreenProps> = ({
    organizationCode,
    onClose
}) => {
    const [yearGroups, setYearGroups] = useState<string[]>([]);
    const [timetables, setTimetables] = useState<Record<string, YearGroupTimetable | null>>({});
    const [classrooms, setClassrooms] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Setup state
    const [showSetup, setShowSetup] = useState(false);
    const [setupYearGroup, setSetupYearGroup] = useState<string>('');
    const [setupTerm, setSetupTerm] = useState<string>('Term 1 2026');
    const [setupStartDate, setSetupStartDate] = useState<string>('2026-01-06'); // Default to a Monday
    const [setupWeeks, setSetupWeeks] = useState<number>(10);
    const [startDateError, setStartDateError] = useState<string>('');

    // Helper function to check if a date is Monday and format it
    const getDateInfo = (dateStr: string) => {
        if (!dateStr) return { isMonday: false, displayText: '', dayName: '' };
        const date = new Date(dateStr + 'T12:00:00'); // Use noon to avoid timezone issues
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = dayNames[date.getDay()];
        const isMonday = date.getDay() === 1;
        const displayText = date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' });
        return { isMonday, displayText, dayName };
    };

    // Handle start date change with Monday validation
    const handleStartDateChange = (newDate: string) => {
        setSetupStartDate(newDate);
        const { isMonday, dayName } = getDateInfo(newDate);
        if (!isMonday) {
            setStartDateError(`⚠️ ${dayName} is not a Monday! Timetables must start on a Monday to align correctly with the week schedule.`);
        } else {
            setStartDateError('');
        }
    };

    // Editor state
    const [showEditor, setShowEditor] = useState(false);
    const [editingTimetable, setEditingTimetable] = useState<YearGroupTimetable | null>(null);
    const [currentWeek, setCurrentWeek] = useState<number>(1);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            // Load classrooms for this organization
            const classesQuery = query(
                collection(db, 'classes'),
                where('organizationCode', '==', organizationCode)
            );
            const classesSnapshot = await getDocs(classesQuery);
            const classroomsData: any[] = classesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setClassrooms(classroomsData);

            // Extract unique year groups
            const uniqueYearGroups = [...new Set(
                classroomsData
                    .map(c => c.yearGroup || 'Unknown')
                    .filter(yg => yg !== 'Unknown')
            )].sort();
            setYearGroups(uniqueYearGroups);

            // Load existing timetables for each year group
            const timetablesMap: Record<string, YearGroupTimetable | null> = {};
            for (const yg of uniqueYearGroups) {
                const timetableId = `${organizationCode}_${yg}`;
                const timetableDoc = await getDoc(doc(db, 'yearGroupTimetables', timetableId));
                if (timetableDoc.exists()) {
                    const data = timetableDoc.data();

                    // Normalize weeks object - Firestore stores keys as strings
                    const weeksRaw = data.weeks || {};
                    const weeks: Record<number, any[]> = {};
                    for (const key in weeksRaw) {
                        const weekNum = parseInt(key, 10);
                        if (!isNaN(weekNum) && Array.isArray(weeksRaw[key])) {
                            weeks[weekNum] = weeksRaw[key];
                        }
                    }

                    timetablesMap[yg] = {
                        id: timetableDoc.id,
                        yearGroup: data.yearGroup || yg,
                        term: data.term || '',
                        termStartDate: data.termStartDate || '',
                        termWeeks: data.termWeeks || 0,
                        weeks: weeks,
                        classrooms: classroomsData.filter(c => c.yearGroup === yg),
                        createdAt: data.createdAt,
                        updatedAt: data.updatedAt
                    } as YearGroupTimetable;
                } else {
                    timetablesMap[yg] = null;
                }
            }
            setTimetables(timetablesMap);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setIsLoading(false);
        }
    }, [organizationCode]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const createTimetable = async () => {
        if (!setupYearGroup || !setupTerm || !setupStartDate) return;

        try {
            const timetableId = `${organizationCode}_${setupYearGroup}`;
            const initialWeeks: Record<number, any[]> = {};
            for (let i = 1; i <= setupWeeks; i++) {
                initialWeeks[i] = [];
            }

            const timetableData = {
                yearGroup: setupYearGroup,
                organizationCode,
                term: setupTerm,
                termStartDate: setupStartDate,
                termWeeks: setupWeeks,
                weeks: initialWeeks,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            };

            await setDoc(doc(db, 'yearGroupTimetables', timetableId), timetableData);

            // Reload data
            await loadData();
            setShowSetup(false);

            // Open editor for the new timetable
            setEditingTimetable({
                id: timetableId,
                ...timetableData,
                classrooms: classrooms.filter(c => c.yearGroup === setupYearGroup)
            });
            setCurrentWeek(1);
            setShowEditor(true);
        } catch (error) {
            console.error('Error creating timetable:', error);
            alert('Failed to create timetable');
        }
    };

    const deleteTimetable = async (yearGroup: string) => {
        if (!window.confirm(`Delete timetable for ${yearGroup}? This will remove the timetable for all classrooms in this year group.`)) {
            return;
        }

        try {
            const timetableId = `${organizationCode}_${yearGroup}`;
            await deleteDoc(doc(db, 'yearGroupTimetables', timetableId));

            // Also clean up any old timetable entries
            const oldTimetablesQuery = query(
                collection(db, 'timetables'),
                where('type', '==', 'yearGroup'),
                where('targetId', '==', yearGroup)
            );
            const oldTimetablesSnapshot = await getDocs(oldTimetablesQuery);
            const deletePromises = oldTimetablesSnapshot.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(deletePromises);

            console.log(`[Timetable] Deleted timetable for ${yearGroup}`);
            await loadData();
        } catch (error) {
            console.error('Error deleting timetable:', error);
            alert('Failed to delete timetable');
        }
    };

    const openEditor = async (yearGroup: string) => {
        try {
            const timetableId = `${organizationCode}_${yearGroup}`;
            const timetableDoc = await getDoc(doc(db, 'yearGroupTimetables', timetableId));

            if (timetableDoc.exists()) {
                const data = timetableDoc.data();

                // Normalize weeks object
                const weeksRaw = data.weeks || {};
                const weeks: Record<number, any[]> = {};
                for (const key in weeksRaw) {
                    const weekNum = parseInt(key, 10);
                    if (!isNaN(weekNum) && Array.isArray(weeksRaw[key])) {
                        weeks[weekNum] = weeksRaw[key];
                    }
                }

                const freshTimetable: YearGroupTimetable = {
                    id: timetableDoc.id,
                    yearGroup: data.yearGroup || yearGroup,
                    term: data.term || 'Term 1 2026',
                    termStartDate: data.termStartDate || '2026-01-05',
                    termWeeks: data.termWeeks || 10,
                    weeks: weeks,
                    classrooms: classrooms.filter(c => c.yearGroup === yearGroup),
                    createdAt: data.createdAt,
                    updatedAt: data.updatedAt
                } as YearGroupTimetable;

                console.log('[Timetable] Opening editor:', {
                    yearGroup,
                    weeksCount: Object.keys(weeks).length,
                    week1Events: weeks[1]?.length || 0
                });

                setEditingTimetable(freshTimetable);
                setCurrentWeek(1);
                setShowEditor(true);
                setTimetables(prev => ({ ...prev, [yearGroup]: freshTimetable }));
            } else {
                alert('Timetable not found. Please refresh and try again.');
            }
        } catch (error) {
            console.error('Error loading timetable:', error);
            alert('Failed to load timetable. Please try again.');
        }
    };

    const openSetup = (yearGroup: string) => {
        setSetupYearGroup(yearGroup);
        setSetupTerm('Term 1 2026');
        // Default to next Monday
        const today = new Date();
        const daysUntilMonday = (8 - today.getDay()) % 7 || 7; // Days until next Monday
        const nextMonday = new Date(today);
        nextMonday.setDate(today.getDate() + daysUntilMonday);
        const defaultDate = nextMonday.toISOString().split('T')[0];
        setSetupStartDate(defaultDate);
        setStartDateError('');
        setSetupWeeks(10);
        setShowSetup(true);
    };

    const getWeekStartDate = (weekNumber: number) => {
        if (!editingTimetable?.termStartDate) return '';
        // Parse the date with time to avoid UTC interpretation issues
        // "YYYY-MM-DD" alone is treated as UTC, causing timezone shifts
        const startDate = new Date(editingTimetable.termStartDate + 'T12:00:00');
        startDate.setDate(startDate.getDate() + (weekNumber - 1) * 7);
        // Return as local date string YYYY-MM-DD
        const year = startDate.getFullYear();
        const month = String(startDate.getMonth() + 1).padStart(2, '0');
        const day = String(startDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const saveWeekEvents = async (weekNumber: number, events: any[]) => {
        if (!editingTimetable) return;

        try {
            const formattedEvents = events.map(evt => ({
                id: evt.id || `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                title: evt.title || '',
                start: evt.start,
                end: evt.end,
                teacher: evt.teacher || evt.extendedProps?.teacher || '',
                room: evt.room || evt.extendedProps?.room || '',
                type: evt.type || evt.extendedProps?.type || 'lesson'
            }));

            const updatedWeeks = { ...editingTimetable.weeks, [weekNumber]: formattedEvents };
            const timetableId = `${organizationCode}_${editingTimetable.yearGroup}`;

            const timetableData = {
                yearGroup: editingTimetable.yearGroup,
                organizationCode: organizationCode,
                term: editingTimetable.term,
                termStartDate: editingTimetable.termStartDate,
                termWeeks: editingTimetable.termWeeks,
                weeks: updatedWeeks,
                createdAt: editingTimetable.createdAt || Timestamp.now(),
                updatedAt: Timestamp.now()
            };

            await setDoc(doc(db, 'yearGroupTimetables', timetableId), timetableData);

            // Update local state
            setEditingTimetable(prev => prev ? { ...prev, weeks: updatedWeeks } : null);
            setTimetables(prev => {
                const updated = { ...prev };
                if (updated[editingTimetable.yearGroup]) {
                    updated[editingTimetable.yearGroup] = {
                        ...updated[editingTimetable.yearGroup]!,
                        weeks: updatedWeeks
                    };
                }
                return updated;
            });

            console.log('[Timetable] Saved week events:', {
                timetableId,
                weekNumber,
                eventCount: formattedEvents.length
            });
        } catch (error) {
            console.error('Error saving week:', error);
            throw error;
        }
    };

    const copyWeekToAll = async (fromWeek: number) => {
        if (!editingTimetable) return;

        if (!window.confirm(`Copy Week ${fromWeek}'s schedule to ALL other weeks? This will overwrite existing events in other weeks.`)) {
            return;
        }

        try {
            const sourceEvents = editingTimetable.weeks[fromWeek] || [];
            const updatedWeeks = { ...editingTimetable.weeks };

            for (let i = 1; i <= editingTimetable.termWeeks; i++) {
                if (i !== fromWeek) {
                    updatedWeeks[i] = sourceEvents.map((evt: any) => ({
                        ...evt,
                        id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${i}`
                    }));
                }
            }

            const timetableId = `${organizationCode}_${editingTimetable.yearGroup}`;
            const timetableData = {
                yearGroup: editingTimetable.yearGroup,
                organizationCode: organizationCode,
                term: editingTimetable.term,
                termStartDate: editingTimetable.termStartDate,
                termWeeks: editingTimetable.termWeeks,
                weeks: updatedWeeks,
                createdAt: editingTimetable.createdAt || Timestamp.now(),
                updatedAt: Timestamp.now()
            };

            await setDoc(doc(db, 'yearGroupTimetables', timetableId), timetableData);

            setEditingTimetable(prev => prev ? { ...prev, weeks: updatedWeeks } : null);
            alert(`Week ${fromWeek} copied to all other weeks!`);
        } catch (error) {
            console.error('Error copying week:', error);
            alert('Failed to copy week');
        }
    };

    // Editor View - Full screen with week tabs
    if (showEditor && editingTimetable) {
        const currentEvents = editingTimetable.weeks[currentWeek] || [];

        return (
            <div className="timetable-editor-fullpage">
                {/* Header */}
                <div className="timetable-editor-header">
                    <button className="back-btn-large" onClick={() => { setShowEditor(false); loadData(); }}>
                        ← Back
                    </button>
                    <div className="header-info">
                        <h1>📅 {editingTimetable.yearGroup} Timetable</h1>
                        <span className="term-badge">{editingTimetable.term} • Starts {editingTimetable.termStartDate}</span>
                    </div>
                    <div className="header-actions">
                        <button
                            className="copy-all-btn"
                            onClick={() => copyWeekToAll(currentWeek)}
                            title="Copy this week's schedule to all other weeks"
                        >
                            📋 Copy Week {currentWeek} to All
                        </button>
                    </div>
                </div>

                {/* Week Tabs */}
                <div className="week-tabs-bar">
                    {Array.from({ length: editingTimetable.termWeeks }, (_, i) => i + 1).map(week => {
                        const eventCount = editingTimetable.weeks[week]?.length || 0;
                        return (
                            <button
                                key={week}
                                className={`week-tab-btn ${currentWeek === week ? 'active' : ''} ${eventCount > 0 ? 'has-events' : ''}`}
                                onClick={() => setCurrentWeek(week)}
                            >
                                <span className="week-label">Week {week}</span>
                                {eventCount > 0 && <span className="event-badge">{eventCount}</span>}
                            </button>
                        );
                    })}
                </div>

                {/* Current Week Info */}
                <div className="current-week-info">
                    <div className="week-details">
                        <h2>Week {currentWeek}</h2>
                        <span className="week-dates">Starting {getWeekStartDate(currentWeek)}</span>
                        <span className="event-count">{currentEvents.length} lessons/events</span>
                    </div>
                    <div className="week-nav">
                        <button
                            className="nav-arrow"
                            disabled={currentWeek <= 1}
                            onClick={() => setCurrentWeek(curr => curr - 1)}
                        >
                            ← Previous
                        </button>
                        <button
                            className="nav-arrow"
                            disabled={currentWeek >= editingTimetable.termWeeks}
                            onClick={() => setCurrentWeek(curr => curr + 1)}
                        >
                            Next →
                        </button>
                    </div>
                </div>

                {/* Calendar Editor */}
                <div className="calendar-container">
                    <TimetableEditor
                        key={`week-${currentWeek}-${editingTimetable.id}-${currentEvents.length}`}
                        targetType="yearGroup"
                        targetId={editingTimetable.yearGroup}
                        targetName={`${editingTimetable.yearGroup} - Week ${currentWeek}`}
                        term={editingTimetable.term}
                        weekNumber={currentWeek}
                        weekStartDate={getWeekStartDate(currentWeek)}
                        initialEvents={currentEvents}
                        onClose={() => { setShowEditor(false); loadData(); }}
                        onSave={async (events) => {
                            await saveWeekEvents(currentWeek, events);
                        }}
                        embedded={true}
                    />
                </div>

                {/* Affected Classrooms */}
                <div className="affected-classrooms">
                    <h4>🏫 Affected Classrooms ({editingTimetable.classrooms.length})</h4>
                    <div className="classroom-list">
                        {editingTimetable.classrooms.map((classroom: any) => (
                            <span key={classroom.id} className="classroom-chip">
                                {classroom.className}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Setup Modal
    if (showSetup) {
        return (
            <div className="timetable-management-screen">
                <div className="management-header">
                    <button className="back-btn" onClick={() => setShowSetup(false)}>
                        ← Back
                    </button>
                    <h1>📅 Create Timetable for {setupYearGroup}</h1>
                </div>

                <div className="setup-form">
                    <div className="form-group">
                        <label>Term Name</label>
                        <input
                            type="text"
                            value={setupTerm}
                            onChange={(e) => setSetupTerm(e.target.value)}
                            placeholder="e.g., Term 1 2026"
                        />
                    </div>
                    <div className="form-group">
                        <label>Term Start Date (Must be a Monday)</label>
                        <input
                            type="date"
                            value={setupStartDate}
                            onChange={(e) => handleStartDateChange(e.target.value)}
                        />
                        {setupStartDate && (
                            <div className={`date-display ${getDateInfo(setupStartDate).isMonday ? 'valid' : 'invalid'}`}>
                                📅 {getDateInfo(setupStartDate).displayText}
                            </div>
                        )}
                        {startDateError && (
                            <div className="date-error">
                                {startDateError}
                            </div>
                        )}
                    </div>
                    <div className="form-group">
                        <label>Number of Weeks</label>
                        <input
                            type="number"
                            value={setupWeeks}
                            onChange={(e) => setSetupWeeks(Math.max(1, Math.min(52, parseInt(e.target.value) || 10)))}
                            min="1"
                            max="52"
                        />
                    </div>
                    <div className="form-actions">
                        <button className="cancel-btn" onClick={() => setShowSetup(false)}>
                            Cancel
                        </button>
                        <button
                            className="create-btn"
                            onClick={createTimetable}
                            disabled={!getDateInfo(setupStartDate).isMonday}
                            title={!getDateInfo(setupStartDate).isMonday ? 'Start date must be a Monday' : 'Create timetable'}
                        >
                            Create Timetable
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Main View - Year Groups Grid
    return (
        <div className="timetable-management-screen">
            <div className="management-header">
                <button className="back-btn" onClick={onClose}>
                    ← Back to Dashboard
                </button>
                <h1>📅 Timetable Management</h1>
                <p>Manage timetables for each year group. Changes apply to all classrooms in that year group.</p>
            </div>

            {isLoading ? (
                <div className="loading-state">Loading...</div>
            ) : (
                <div className="year-groups-grid">
                    {yearGroups.length === 0 ? (
                        <div className="empty-state">
                            <p>No year groups found. Create classrooms with year groups first.</p>
                        </div>
                    ) : (
                        yearGroups.map(yearGroup => {
                            const timetable = timetables[yearGroup];
                            const classroomsInGroup = classrooms.filter(c => c.yearGroup === yearGroup);
                            const totalEvents = timetable
                                ? Object.values(timetable.weeks).reduce((sum, events) => sum + (events?.length || 0), 0)
                                : 0;

                            return (
                                <div key={yearGroup} className={`year-group-card ${timetable ? 'has-timetable' : ''}`}>
                                    <div className="yg-header">
                                        <h3>📚 {yearGroup}</h3>
                                        <span className="classroom-count">{classroomsInGroup.length} classes</span>
                                    </div>

                                    {timetable ? (
                                        <div className="yg-timetable-info">
                                            <div className="timetable-stats">
                                                <div className="stat">
                                                    <span className="stat-value">{timetable.term}</span>
                                                    <span className="stat-label">Term</span>
                                                </div>
                                                <div className="stat">
                                                    <span className="stat-value">{timetable.termWeeks}</span>
                                                    <span className="stat-label">Weeks</span>
                                                </div>
                                                <div className="stat">
                                                    <span className="stat-value">{totalEvents}</span>
                                                    <span className="stat-label">Total Events</span>
                                                </div>
                                            </div>

                                            <div className="yg-actions">
                                                <button
                                                    className="edit-timetable-btn"
                                                    onClick={() => openEditor(yearGroup)}
                                                >
                                                    ✏️ Edit Timetable
                                                </button>
                                                <button
                                                    className="delete-timetable-btn"
                                                    onClick={() => deleteTimetable(yearGroup)}
                                                >
                                                    🗑️ Delete
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="yg-no-timetable">
                                            <p>No timetable configured</p>
                                            <button
                                                className="setup-timetable-btn"
                                                onClick={() => openSetup(yearGroup)}
                                            >
                                                ➕ Create Timetable
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

export default TimetableManagementScreen;
